import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, CircleDashed, AlertTriangle, ArrowRight, ClipboardCheck } from 'lucide-react';
import { Button } from '../../ui/button';
import { API, auth, err } from '../invoices/invoiceApi';
import { PublishReminder } from './PublishReminder';

const STATUS = {
  ready: { label: 'Verified / Ready', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 },
  in_progress: { label: 'In Progress', tone: 'text-amber-700 bg-amber-50 border-amber-200', Icon: CircleDashed },
  setup_required: { label: 'Setup Required', tone: 'text-red-700 bg-red-50 border-red-200', Icon: AlertTriangle },
};

const Item = ({ it, onOpen, onConfirmTax, tax }) => {
  const s = STATUS[it.status];
  return (
    <div className="bg-white border rounded-lg p-4 flex items-start gap-3" data-testid={`setup-item-${it.key}`}>
      <s.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${s.tone.split(' ')[0]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2"><p className="font-semibold">{it.label}</p>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${s.tone}`} data-testid={`setup-status-${it.key}`}>{s.label}</span></div>
        <p className="text-sm text-neutral-600 mt-1" data-testid={`setup-detail-${it.key}`}>{it.detail}</p>
        {it.key === 'tax' && !tax && <Button size="sm" variant="outline" className="mt-2" onClick={onConfirmTax} data-testid="setup-tax-confirm">I've reviewed the tax settings: confirm</Button>}
      </div>
      <Button size="sm" variant="ghost" onClick={() => onOpen(it.tab)} data-testid={`setup-open-${it.key}`}>Open<ArrowRight className="w-4 h-4 ml-1" /></Button>
    </div>
  );
};

export const useSetupChecklist = (token, enabled = true) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => (enabled ? axios.get(`${API}/admin/setup-checklist`, auth(token)).then((r) => setD(r.data)).catch(() => {}) : null), [token, enabled]);
  useEffect(() => { load(); }, [load]);
  return [d, setD, load];
};

export default function SetupChecklistTab({ token, onNavigate }) {
  const [d, setD] = useSetupChecklist(token);
  if (!d) return <p className="text-sm text-neutral-500" data-testid="setup-loading">Loading…</p>;
  const confirmTax = () => axios.post(`${API}/admin/setup-checklist/tax-confirm`, {}, auth(token)).then((r) => { setD(r.data); toast.success('Tax settings confirmed'); }).catch((e) => toast.error(err(e, 'Could not confirm')));
  const done = d.items.filter((i) => i.status === 'ready').length;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="setup-checklist-tab">
      <h2 className="text-lg font-semibold flex items-center gap-2"><ClipboardCheck className="w-5 h-5" />Setup Checklist</h2>
      <div className={`rounded-lg border p-4 text-sm ${d.production_ready ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="setup-overall">
        <p className="font-semibold">{d.production_ready ? 'Ready for production' : 'Not ready for production'}</p>
        <p className="text-neutral-600">{done} of {d.items.length} ready{d.not_ready.length ? `. Still to do: ${d.not_ready.join(', ')}` : ''}</p>
      </div>
      <PublishReminder r={d.publish_reminder} token={token} onData={setD} />
      {d.items.map((it) => <Item key={it.key} it={it} onOpen={onNavigate} onConfirmTax={confirmTax} tax={d.tax_confirmed} />)}
    </div>
  );
}
